// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { type AgentName } from "./types"

/** The one runtime-resolution mode that applies to a given frontend run. */
export enum FrontendMode {
  /** Default local Vite mode for either a host-run or Compose-run dev server. */
  NormalCompose = "normal-compose",
  /** `npm run dev -- --mode deployed` against deployed runtimes. */
  ViteDeployed = "vite-deployed",
  /** Production `vite build` served from S3/CloudFront. */
  DeployedBuild = "deployed-build",
}

/** Shape of the parsed aws-exports.json relevant to runtime resolution. */
export interface AwsExports {
  readonly auth0Domain?: string
  readonly auth0ClientId?: string
  readonly auth0Audience?: string
  readonly awsRegion?: string
  readonly agentPattern?: string
  /** Local mode: canonical assistant ID -> same-origin proxy path. */
  readonly localAgentUrls?: Partial<Record<AgentName, string>>
  /** Deployed build: canonical assistant ID -> AgentCore Runtime ARN. */
  readonly agentRuntimeArns?: Partial<Record<AgentName, string>>
}

/** Exactly one resolved invocation target for the active assistant. */
export type ResolvedTarget =
  | { readonly kind: "localUrl"; readonly localAgentUrl: string }
  | { readonly kind: "runtimeArn"; readonly runtimeArn: string }

/**
 * Vite statically replaces `import.meta.env.VITE_*` at build time, so the three
 * overrides MUST be referenced by literal name (no dynamic indexing). This
 * record maps each canonical assistant ID to its build-time override value.
 */
export const VITE_OVERRIDES: Record<AgentName, string | undefined> = {
  "deep-literature-review": import.meta.env.VITE_AGENT_RUNTIME_ARN_DEEP_LITERATURE_REVIEW,
  "exploratory-literature-review": import.meta.env.VITE_AGENT_RUNTIME_ARN_EXPLORATORY_LITERATURE_REVIEW,
  "survey-variable-discovery": import.meta.env.VITE_AGENT_RUNTIME_ARN_SURVEY_VARIABLE_DISCOVERY,
}

/**
 * Classify the current frontend run into exactly one FrontendMode.
 *
 * Precedence is fixed and mutually exclusive: a production build is always a
 * Deployed_Build; a dev server started with `--mode deployed` is
 * Vite_Deployed_Mode; every other dev server run uses the existing
 * NormalCompose value. No run is ever classified into more than one mode.
 *
 * @param env - The `import.meta.env` object for the current run.
 * @returns The single applicable FrontendMode.
 */
export function detectFrontendMode(env: ImportMetaEnv): FrontendMode {
  if (env.PROD) return FrontendMode.DeployedBuild
  if (env.MODE === "deployed") return FrontendMode.ViteDeployed
  return FrontendMode.NormalCompose
}

/**
 * Decide whether the frontend may skip Auth0 for local development.
 *
 * No_Auth_Mode requires all three conditions simultaneously: the default local
 * mode is active, a non-empty Local_URL_Map is present, and no Auth0 domain is
 * configured. A checked-in local map therefore cannot disable authentication
 * in Vite_Deployed_Mode or a Deployed_Build.
 *
 * @param mode - The single applicable FrontendMode (from detectFrontendMode).
 * @param localAgentUrls - The Local_URL_Map from aws-exports.json, if any.
 * @param auth0Domain - The configured Auth0 domain (empty string when unset).
 * @returns True only for the unauthenticated default local configuration.
 */
export function shouldUseNoAuth({
  mode,
  localAgentUrls,
  auth0Domain,
}: {
  mode: FrontendMode
  localAgentUrls?: Partial<Record<AgentName, string>>
  auth0Domain: string
}): boolean {
  return (
    mode === FrontendMode.NormalCompose &&
    !!localAgentUrls &&
    Object.keys(localAgentUrls).length > 0 &&
    auth0Domain.trim().length === 0
  )
}

/**
 * Treat a config value as present only when it is a non-empty, non-whitespace string.
 *
 * @param value - The candidate map entry or override value.
 * @returns The trimmed value when usable, otherwise null.
 */
function usableTarget(value: string | undefined | null): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Throw a targeted configuration error naming both the assistant and the mode.
 *
 * @param activeAssistantId - The assistant that has no usable target.
 * @param mode - The active mode whose single source was consulted.
 * @throws Always throws; the `never` return marks the call as terminal.
 */
function throwMissingTarget(activeAssistantId: AgentName, mode: FrontendMode): never {
  throw new Error(
    `No invocation target configured for assistant "${activeAssistantId}" ` +
      `in mode "${mode}".`,
  )
}

/**
 * Resolve exactly one invocation target for the active assistant, consulting
 * only the source that belongs to the active mode.
 *
 * Each branch reads exactly one map and never falls back to another mode's
 * source, so a deployed run can never resolve a local URL. Values are trimmed
 * before the emptiness check, so a whitespace-only entry counts as unset.
 *
 * @param mode - The single applicable FrontendMode (from detectFrontendMode).
 * @param activeAssistantId - The assistant the user currently has open.
 * @param config - The parsed aws-exports.json.
 * @param overrides - The statically-referenced Vite overrides (VITE_OVERRIDES).
 * @returns The single ResolvedTarget for the active assistant.
 * @throws Error naming the assistant and mode when the active source is unset.
 */
export function resolveInvocationTarget({
  mode,
  activeAssistantId,
  config,
  overrides,
}: {
  mode: FrontendMode
  activeAssistantId: AgentName
  config: AwsExports
  overrides: Record<AgentName, string | undefined>
}): ResolvedTarget {
  switch (mode) {
    case FrontendMode.NormalCompose: {
      const url = usableTarget(config.localAgentUrls?.[activeAssistantId])
      if (url === null) throwMissingTarget(activeAssistantId, mode)
      return { kind: "localUrl", localAgentUrl: url }
    }
    case FrontendMode.ViteDeployed: {
      const arn = usableTarget(overrides[activeAssistantId])
      if (arn === null) throwMissingTarget(activeAssistantId, mode)
      return { kind: "runtimeArn", runtimeArn: arn }
    }
    case FrontendMode.DeployedBuild: {
      const arn = usableTarget(config.agentRuntimeArns?.[activeAssistantId])
      if (arn === null) throwMissingTarget(activeAssistantId, mode)
      return { kind: "runtimeArn", runtimeArn: arn }
    }
  }
}
